"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import {
  Clock, Sparkles, Moon, Sunrise, Sun, Sunset, Flame, Sword,
  Flower2, Landmark, PartyPopper, Star, Lamp, Wand2, Flower,
  Bird, Heart, Phone, Waves, CalendarDays, ChevronRight,
  MapPin, Zap, Check, ArrowRight, AlarmClock, Info,
} from "lucide-react";

const emojiToIcon: { [key: string]: React.ReactNode } = {
  "🌙": <Moon className="h-5 w-5" />,
  "🌅": <Sunrise className="h-5 w-5" />,
  "☀️": <Sun className="h-5 w-5" />,
  "🌆": <Sunset className="h-5 w-5" />,
  "🪔": <Lamp className="h-5 w-5" />,
  "🔱": <Wand2 className="h-5 w-5" />,
  "🌺": <Flower className="h-5 w-5" />,
  "🌑": <Moon className="h-5 w-5" />,
  "🐘": <Bird className="h-5 w-5" />,
  "🌒": <Moon className="h-4 w-4" />,
  "🌓": <Moon className="h-4 w-4" />,
  "🌔": <Moon className="h-4 w-4" />,
  "🌕": <Sun className="h-5 w-5" />,
  "🌖": <Moon className="h-4 w-4" />,
  "🌗": <Moon className="h-4 w-4" />,
  "🌘": <Moon className="h-4 w-4" />,
  "🙏": <Heart className="h-5 w-5" />,
  "📞": <Phone className="h-4 w-4" />,
  "🌊": <Waves className="h-5 w-5" />,
  "🔥": <Flame className="h-5 w-5" />,
  "⚔️": <Sword className="h-5 w-5" />,
  "🎉": <PartyPopper className="h-5 w-5" />,
  "🌸": <Flower2 className="h-5 w-5" />,
  "🎺": <Sparkles className="h-5 w-5" />,
  "✨": <Sparkles className="h-5 w-5" />,
};

const content = {
  en: {
    badge: "Sacred Schedule",
    title: "Pooja Timings",
    subtitle: "Every ritual, every moment -  in devotion to the Divine",
    tabs: ["Daily", "Weekly", "Monthly", "Amavasai", "Festivals"],

    daily: {
      title: "Daily Pooja Schedule",
      subtitle: "Performed every day of the year without exception",
      timings: [
        { icon: "🌙", name: "Brahma Muhurtha Pooja", tamil: "பிரம்ம முகூர்த்த பூஜை", time: "4:30 AM – 6:00 AM", desc: "The most auspicious hour of the day. The deity is awakened with sacred hymns and offerings.", highlight: true, tag: "Daily" },
        { icon: "🌅", name: "Morning Darshan Opens", tamil: "காலை தரிசனம் தொடங்கும்", time: "4:00 AM", desc: "Temple doors open for all devotees to receive morning blessings.", highlight: false, tag: "Daily" },
        { icon: "☀️", name: "Morning Darshan Closes", tamil: "காலை தரிசனம் முடியும்", time: "12:00 PM", desc: "Morning darshan concludes. The deity rests during midday.", highlight: false, tag: "Daily" },
        { icon: "🌆", name: "Evening Darshan Opens", tamil: "மாலை தரிசனம் தொடங்கும்", time: "4:00 PM", desc: "Temple reopens for evening blessings and lamp lighting.", highlight: false, tag: "Daily" },
        { icon: "🪔", name: "Evening Darshan Closes", tamil: "மாலை தரிசனம் முடியும்", time: "7:00 PM", desc: "Final blessings for the day. The temple closes after evening prayers.", highlight: false, tag: "Daily" },
      ],
    },

    weekly: {
      title: "Weekly Special Days",
      subtitle: "Extra-special rituals on auspicious weekdays",
      days: [
        { day: "Tuesday", tamilDay: "செவ்வாய்", icon: "🔱", color: "amber", timing: "Full Day Open", desc: "Dedicated to Lord Karuppanasamy. Special abhishekam and alankaram performed.", highlight: true },
        { day: "Friday", tamilDay: "வெள்ளி", icon: "🌺", color: "orange", timing: "Full Day Open", desc: "Dedicated to Kaali Amman. Special flower decoration and kumkum archana.", highlight: true },
        { day: "Monday", tamilDay: "திங்கள்", icon: "🌙", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "Regular darshan timings. Auspicious for Bairavar and Murugan prayers.", highlight: false },
        { day: "Wednesday", tamilDay: "புதன்", icon: "🐘", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "Regular darshan. Especially auspicious for Vinayagar worship.", highlight: false },
        { day: "Saturday", tamilDay: "சனி", icon: "🌑", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "Regular darshan. Devotees offer sesame oil lamps for Navagraha blessings.", highlight: false },
      ],
    },

    monthly: {
      title: "Monthly Special Poojas",
      subtitle: "Sacred observances that recur every lunar month",
      items: [
        { icon: "🌑", name: "Ashtami", tamilName: "அஷ்டமி", timing: "4:30 PM – 6:00 PM", type: "Evening Special Pooja", desc: "The eighth lunar day. Special evening pooja with elaborate rituals for Karuppanasamy.", color: "amber" },
        { icon: "🌒", name: "Navami", tamilName: "நவமி", timing: "4:30 PM – 6:00 PM", type: "Evening Special Pooja", desc: "The ninth lunar day. Sacred rituals and special prasadam distribution.", color: "orange" },
        { icon: "🌕", name: "Pavarnamni (Pournami)", tamilName: "பவுர்ணமி", timing: "Full Day Open", type: "Full Day Celebration", desc: "Full moon day. Grand celebration with special midnight pooja and procession.", color: "yellow" },
        { icon: "🌑", name: "Amavasai", tamilName: "அமாவாசை", timing: "Full Day Open", type: "Full Day Open", desc: "New moon day. Ancestral rituals and special prayers for departed souls.", color: "amber" },
      ],
    },

    amavasai: {
      title: "Amavasai -  New Moon Day",
      subtitle: "The most significant lunar observance at our temple",
      significance: "Amavasai holds deep spiritual significance at the 18m Padi Karuppanasamy Temple. Devotees gather from surrounding villages to offer prayers for their ancestors and seek blessings.",
      schedule: [
        { time: "4:00 AM", event: "Temple Opens", desc: "Early opening for devotees who observe fasting rituals", icon: "🌑" },
        { time: "4:30 AM", event: "Brahma Muhurtha Pooja", desc: "Special ancestral prayers (Pitru Tarpana) offered", icon: "🪔" },
        { time: "Morning", event: "Continuous Darshan", desc: "Uninterrupted darshan throughout the morning", icon: "🙏" },
        { time: "Afternoon", event: "Special Abhishekam", desc: "Grand ritual bathing of the deity with sacred substances", icon: "🌊" },
        { time: "Evening", event: "Dheepaaradhana", desc: "Lamp lighting ceremony with hundreds of oil lamps", icon: "🔥" },
        { time: "7:00 PM", event: "Temple Closes", desc: "Final blessings and closure after a full day of devotion", icon: "🌙" },
      ],
    },

    festivals: {
      title: "Festival Poojas",
      subtitle: "Annual celebrations of divine glory",
      items: [
        { icon: "🎺", name: "Amavasai", tamilName: "அமாவாசை", timing: "Full Day", freq: "Monthly", color: "amber", desc: "New moon observance. Full day darshan and special ancestral prayers." },
        { icon: "🌕", name: "Pavarnamni", tamilName: "பவுர்ணமி", timing: "Full Day", freq: "Monthly", color: "yellow", desc: "Full moon celebration with grand rituals and lamp procession." },
        { icon: "🔱", name: "Ashtami", tamilName: "அஷ்டமி", timing: "4:30 PM – 6:00 PM", freq: "Monthly", color: "orange", desc: "Eight lunar day special evening pooja with elaborate alankaram." },
        { icon: "⚔️", name: "Navami", tamilName: "நவமி", timing: "4:30 PM – 6:00 PM", freq: "Monthly", color: "red", desc: "Ninth lunar day rituals with sacred recitations and offerings." },
        { icon: "🎉", name: "Temple Anniversary", tamilName: "கோவில் ஆண்டு விழா", timing: "Full Day Celebration", freq: "Yearly (2014)", color: "amber", desc: "Annual celebration since 2014 inauguration. Grand procession and community feast." },
        { icon: "🌸", name: "Tuesday Special", tamilName: "செவ்வாய் சிறப்பு", timing: "Full Day", freq: "Weekly", color: "orange", desc: "Weekly special worship day for Karuppanasamy with full day access." },
      ],
    },

    openNote: "Temple is open all 7 days. Morning: 4 AM – 12 PM | Evening: 4 PM – 7 PM",
    contactNote: "For special pooja bookings call: 9597255401 / 9361986095",
  },

  ta: {
    badge: "புனித அட்டவணை",
    title: "பூஜை நேரங்கள்",
    subtitle: "ஒவ்வொரு வழிபாடும், ஒவ்வொரு தருணமும் -  தெய்வத்திற்கு அர்ப்பணம்",
    tabs: ["தினசரி", "வாரம்", "மாதம்", "அமாவாசை", "விழாக்கள்"],

    daily: {
      title: "தினசரி பூஜை அட்டவணை",
      subtitle: "ஆண்டு முழுவதும் தினமும் நடைபெறும் வழிபாடுகள்",
      timings: [
        { icon: "🌙", name: "பிரம்ம முகூர்த்த பூஜை", tamil: "Brahma Muhurtha Pooja", time: "4:30 AM – 6:00 AM", desc: "நாளின் மிகவும் புனிதமான நேரம். தெய்வம் புனித மந்திரங்களுடன் எழுப்பப்படுகிறது.", highlight: true, tag: "தினசரி" },
        { icon: "🌅", name: "காலை தரிசனம் தொடங்கும்", tamil: "Morning Darshan Opens", time: "4:00 AM", desc: "அனைத்து பக்தர்களுக்கும் காலை ஆசீர்வாதத்திற்கு கோவில் கதவு திறக்கப்படும்.", highlight: false, tag: "தினசரி" },
        { icon: "☀️", name: "காலை தரிசனம் முடியும்", tamil: "Morning Darshan Closes", time: "12:00 PM", desc: "காலை தரிசனம் நிறைவு பெறும். மதிய வேளையில் தெய்வம் ஓய்வெடுக்கும்.", highlight: false, tag: "தினசரி" },
        { icon: "🌆", name: "மாலை தரிசனம் தொடங்கும்", tamil: "Evening Darshan Opens", time: "4:00 PM", desc: "மாலை ஆசீர்வாதம் மற்றும் விளக்கு ஏற்றலுக்கு கோவில் மீண்டும் திறக்கப்படும்.", highlight: false, tag: "தினசரி" },
        { icon: "🪔", name: "மாலை தரிசனம் முடியும்", tamil: "Evening Darshan Closes", time: "7:00 PM", desc: "நாளின் இறுதி ஆசீர்வாதம். மாலை வழிபாட்டிற்கு பின் கோவில் மூடப்படும்.", highlight: false, tag: "தினசரி" },
      ],
    },

    weekly: {
      title: "வாராந்திர சிறப்பு நாட்கள்",
      subtitle: "புனிதமான வார நாட்களில் சிறப்பு வழிபாடுகள்",
      days: [
        { day: "செவ்வாய்", tamilDay: "Tuesday", icon: "🔱", color: "amber", timing: "முழு நேர தரிசனம்", desc: "கருப்பண்ணசாமிக்கு அர்ப்பணிக்கப்பட்டது. சிறப்பு அபிஷேகம் மற்றும் அலங்காரம் நடைபெறும்.", highlight: true },
        { day: "வெள்ளி", tamilDay: "Friday", icon: "🌺", color: "orange", timing: "முழு நேர தரிசனம்", desc: "காளி அம்மனுக்கு அர்ப்பணிக்கப்பட்டது. சிறப்பு மலர் அலங்காரம் மற்றும் குங்கும அர்ச்சனை.", highlight: true },
        { day: "திங்கள்", tamilDay: "Monday", icon: "🌙", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "வழக்கமான தரிசன நேரங்கள். பைரவர் மற்றும் முருகன் வழிபாட்டிற்கு சுபமான நாள்.", highlight: false },
        { day: "புதன்", tamilDay: "Wednesday", icon: "🐘", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "வழக்கமான தரிசனம். விநாயகர் வழிபாட்டிற்கு குறிப்பாக சுபமானது.", highlight: false },
        { day: "சனி", tamilDay: "Saturday", icon: "🌑", color: "neutral", timing: "4:00 AM – 7:00 PM", desc: "வழக்கமான தரிசனம். நவகிரக ஆசீர்வாதத்திற்கு எள்ளெண்ணெய் தீபம் ஏற்றுவர்.", highlight: false },
      ],
    },

    monthly: {
      title: "மாதாந்திர சிறப்பு பூஜைகள்",
      subtitle: "ஒவ்வொரு சந்திர மாதமும் திரும்பும் புனித நிகழ்வுகள்",
      items: [
        { icon: "🌑", name: "அஷ்டமி", tamilName: "Ashtami", timing: "4:30 PM – 6:00 PM", type: "மாலை சிறப்பு பூஜை", desc: "எட்டாம் சந்திர நாள். கருப்பண்ணசாமிக்கு விரிவான சடங்குகளுடன் சிறப்பு மாலை பூஜை.", color: "amber" },
        { icon: "🌒", name: "நவமி", tamilName: "Navami", timing: "4:30 PM – 6:00 PM", type: "மாலை சிறப்பு பூஜை", desc: "ஒன்பதாம் சந்திர நாள். புனித சடங்குகள் மற்றும் சிறப்பு பிரசாதம் வினியோகம்.", color: "orange" },
        { icon: "🌕", name: "பவுர்ணமி", tamilName: "Pavarnamni", timing: "முழு நேர தரிசனம்", type: "முழு நாள் கொண்டாட்டம்", desc: "பௌர்ணமி நாள். சிறப்பு நள்ளிரவு பூஜை மற்றும் ஊர்வலத்துடன் பெரும் கொண்டாட்டம்.", color: "yellow" },
        { icon: "🌑", name: "அமாவாசை", tamilName: "Amavasai", timing: "முழு நேர தரிசனம்", type: "முழு நாள் திறப்பு", desc: "அமாவாசை நாள். முன்னோர் சடங்குகள் மற்றும் மரணமடைந்தவர்களுக்கான சிறப்பு வழிபாடு.", color: "amber" },
      ],
    },

    amavasai: {
      title: "அமாவாசை -  அமாவாசை நாள்",
      subtitle: "எங்கள் கோவிலில் மிகவும் முக்கியமான சந்திர நிகழ்வு",
      significance: "அமாவாசை 18ம் படி கருப்பண்ணசாமி கோவிலில் ஆழமான ஆன்மீக முக்கியத்துவம் கொண்டது. சுற்றுவட்டார கிராமங்களிலிருந்து பக்தர்கள் முன்னோர்களுக்காக வழிபட்டு ஆசீர்வாதம் பெற வருகின்றனர்.",
      schedule: [
        { time: "4:00 AM", event: "கோவில் திறக்கும்", desc: "விரத சடங்குகளை கடைப்பிடிக்கும் பக்தர்களுக்கு முன்னதாக திறப்பு", icon: "🌑" },
        { time: "4:30 AM", event: "பிரம்ம முகூர்த்த பூஜை", desc: "சிறப்பு முன்னோர் வழிபாடு (பித்ரு தர்ப்பணம்) நடைபெறும்", icon: "🪔" },
        { time: "காலை", event: "தொடர் தரிசனம்", desc: "காலை முழுவதும் இடைவிடாத தரிசனம்", icon: "🙏" },
        { time: "மதியம்", event: "சிறப்பு அபிஷேகம்", desc: "புனித பொருட்களால் தெய்வத்திற்கு பெரும் சடங்கு குளியல்", icon: "🌊" },
        { time: "மாலை", event: "தீபாராதனை", desc: "நூற்றுக்கணக்கான எண்ணெய் தீபங்களுடன் விளக்கு ஏற்றும் சடங்கு", icon: "✨" },
        { time: "7:00 PM", event: "கோவில் மூடும்", desc: "முழு நாள் பக்தி நிறைந்த தரிசனத்திற்கு பிறகு இறுதி ஆசீர்வாதம்", icon: "🌙" },
      ],
    },

    festivals: {
      title: "விழா பூஜைகள்",
      subtitle: "தெய்வீக மகிமையின் ஆண்டு கொண்டாட்டங்கள்",
      items: [
        { icon: "🎺", name: "அமாவாசை", tamilName: "Amavasai", timing: "முழு நாள்", freq: "மாதாந்திரம்", color: "amber", desc: "அமாவாசை நிகழ்வு. முழு நாள் தரிசனம் மற்றும் முன்னோர் வழிபாடு." },
        { icon: "🌕", name: "பவுர்ணமி", tamilName: "Pavarnamni", timing: "முழு நாள்", freq: "மாதாந்திரம்", color: "yellow", desc: "பௌர்ணமி கொண்டாட்டம் பெரும் சடங்கு மற்றும் தீப ஊர்வலத்துடன்." },
        { icon: "🔱", name: "அஷ்டமி", tamilName: "Ashtami", timing: "4:30 PM – 6:00 PM", freq: "மாதாந்திரம்", color: "orange", desc: "எட்டாம் சந்திர நாள் சிறப்பு மாலை பூஜை விரிவான அலங்காரத்துடன்." },
        { icon: "⚔️", name: "நவமி", tamilName: "Navami", timing: "4:30 PM – 6:00 PM", freq: "மாதாந்திரம்", color: "red", desc: "ஒன்பதாம் சந்திர நாள் சடங்குகள் புனித மந்திர ஜபம் மற்றும் நிவேதனம்." },
        { icon: "🎉", name: "கோவில் ஆண்டு விழா", tamilName: "Temple Anniversary", timing: "முழு நாள் கொண்டாட்டம்", freq: "ஆண்டுதோறும் (2014)", color: "amber", desc: "2014 திறப்பிலிருந்து ஆண்டு கொண்டாட்டம். பெரும் ஊர்வலம் மற்றும் சமூக விருந்து." },
        { icon: "🌸", name: "செவ்வாய் சிறப்பு", tamilName: "Tuesday Special", timing: "முழு நாள்", freq: "வாராந்திரம்", color: "orange", desc: "கருப்பண்ணசாமிக்கு வாராந்திர சிறப்பு வழிபாட்டு நாள் முழு நாள் தரிசனத்துடன்." },
      ],
    },

    openNote: "கோவில் வாரம் 7 நாட்களும் திறந்திருக்கும். காலை: 4 AM – 12 PM | மாலை: 4 PM – 7 PM",
    contactNote: "சிறப்பு பூஜை முன்பதிவுக்கு அழைக்கவும்: 9597255401 / 9361986095",
  },
};

const TAB_KEYS = ["daily", "weekly", "monthly", "amavasai", "festivals"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const tabIcons: Record<TabKey, React.ReactNode> = {
  daily: <Clock className="h-4 w-4" />,
  weekly: <CalendarDays className="h-4 w-4" />,
  monthly: <Moon className="h-4 w-4" />,
  amavasai: <Sun className="h-4 w-4" />,
  festivals: <Sparkles className="h-4 w-4" />,
};

const freqColorMap: Record<string, string> = {
  Monthly: "bg-amber-950/50 text-amber-400 border-amber-800/40",
  Yearly: "bg-orange-950/50 text-orange-400 border-orange-800/40",
  Weekly: "bg-yellow-950/50 text-yellow-400 border-yellow-800/40",
  "Yearly (2014)": "bg-orange-950/50 text-orange-400 border-orange-800/40",
  மாதாந்திரம்: "bg-amber-950/50 text-amber-400 border-amber-800/40",
  "ஆண்டுதோறும் (2014)": "bg-orange-950/50 text-orange-400 border-orange-800/40",
  வாராந்திரம்: "bg-yellow-950/50 text-yellow-400 border-yellow-800/40",
};

const monthlyColorBorder: Record<string, string> = {
  amber: "border-amber-700/50 bg-gradient-to-br from-amber-950/30 to-neutral-900",
  orange: "border-orange-700/50 bg-gradient-to-br from-orange-950/30 to-neutral-900",
  yellow: "border-yellow-600/50 bg-gradient-to-br from-yellow-950/30 to-neutral-900",
  red: "border-red-700/50 bg-gradient-to-br from-red-950/30 to-neutral-900",
};

const timelineColors = [
  "from-amber-500 to-amber-700",
  "from-orange-500 to-orange-700",
  "from-yellow-500 to-yellow-700",
  "from-amber-400 to-orange-600",
  "from-orange-400 to-amber-600",
  "from-amber-600 to-yellow-500",
];

export default function PoojaSchedulePage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;
  const [activeTab, setActiveTab] = useState<TabKey>("daily");

  return (
    <main className="bg-[#080604] text-neutral-100 min-h-screen selection:bg-amber-900 selection:text-amber-100 overflow-x-hidden">

      {/* ── HERO ── */}
      <section className="relative min-h-[60vh] flex flex-col items-center justify-center text-center px-4 overflow-hidden">
        {/* Layered atmospheric glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/30 via-[#080604] to-[#080604]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-amber-700/8 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-8 left-12 w-60 h-60 bg-orange-900/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-8 right-12 w-72 h-72 bg-amber-900/8 rounded-full blur-[100px] pointer-events-none" />

        {/* Subtle mandala-like dot pattern */}
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '36px 36px' }}
        />

        <div className="relative z-10 space-y-6 max-w-4xl mx-auto pt-20 pb-12">
          {/* Ornamental divider */}
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-14 bg-gradient-to-r from-transparent to-amber-600/60" />
            <AlarmClock className="h-4 w-4 text-amber-500" />
            <div className="h-px w-14 bg-gradient-to-l from-transparent to-amber-600/60" />
          </div>

          <Badge
            variant="outline"
            className="text-amber-300 border-amber-500/40 bg-amber-950/40 px-6 py-2 text-xs font-medium tracking-[0.3em] uppercase rounded-full"
          >
            {t.badge}
          </Badge>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-none">
            <span className="bg-gradient-to-br from-amber-100 via-yellow-300 to-orange-500 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h1>

          <p className="text-base md:text-lg text-amber-100/40 max-w-xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>

          {/* Live open strip */}
          <div className="inline-flex items-center gap-3 bg-neutral-900/70 border border-neutral-800 rounded-full px-6 py-3 text-xs text-neutral-400 mt-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-md shadow-emerald-500/50 animate-pulse" />
              <span className="text-emerald-400 font-semibold">{language === "ta" ? "திறந்திருக்கும்" : "Open"}</span>
            </div>
            <div className="w-px h-3 bg-neutral-700" />
            <span>{t.openNote}</span>
          </div>
        </div>
      </section>

      {/* ── STICKY TAB NAV ── */}
      <section className="sticky top-0 z-30 bg-[#080604]/95 backdrop-blur-xl border-b border-amber-900/20">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/30 to-transparent" />
        <div className="container mx-auto px-4 max-w-5xl py-4">
          <div className="flex gap-2 overflow-x-auto justify-start md:justify-center pb-1">
            {TAB_KEYS.map((key, i) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border
                  ${activeTab === key
                    ? "bg-amber-600 text-white border-amber-500 shadow-lg shadow-amber-900/40 scale-105"
                    : "bg-neutral-900/60 text-neutral-400 border-neutral-800 hover:border-amber-800/50 hover:text-amber-400 hover:bg-neutral-900"
                  }`}
              >
                <span className={activeTab === key ? "text-white" : "text-amber-600"}>
                  {tabIcons[key]}
                </span>
                {t.tabs[i]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── TAB CONTENT ── */}
      <div className="container mx-auto px-5 lg:px-16 max-w-6xl py-16">

        {/* ── DAILY ── */}
        {activeTab === "daily" && (
          <div className="space-y-12">
            {/* Section header */}
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-600/50" />
                <Clock className="h-4 w-4 text-amber-500" />
                <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-600/50" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-400">{t.daily.title}</h2>
              <p className="text-neutral-500 text-sm">{t.daily.subtitle}</p>
            </div>

            {/* Enhanced timeline */}
            <div className="relative max-w-3xl mx-auto">
              {/* Vertical line */}
              <div className="absolute left-[27px] top-8 bottom-8 w-px bg-gradient-to-b from-amber-600/60 via-amber-800/30 to-transparent hidden md:block" />

              <div className="space-y-4">
                {t.daily.timings.map((item, i) => (
                  <div key={i} className="relative flex gap-5 items-stretch group">
                    {/* Timeline node */}
                    <div className="hidden md:flex shrink-0 flex-col items-center">
                      <div className={`relative z-10 w-[54px] h-[54px] rounded-2xl border-2 flex items-center justify-center transition-all duration-300 group-hover:scale-110
                        ${item.highlight
                          ? "border-amber-500 bg-gradient-to-br from-amber-900/60 to-amber-950 text-amber-300 shadow-lg shadow-amber-900/40"
                          : "border-neutral-700 bg-neutral-900/80 text-amber-500 group-hover:border-amber-800/60"
                        }`}>
                        {emojiToIcon[item.icon]}
                      </div>
                    </div>

                    {/* Card */}
                    <div className={`flex-1 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl overflow-hidden
                      ${item.highlight
                        ? "border-amber-700/50 bg-gradient-to-r from-amber-950/40 via-amber-950/20 to-neutral-900 shadow-lg shadow-amber-950/30"
                        : "border-neutral-800 bg-neutral-900/50 hover:border-neutral-700 hover:bg-neutral-900/70"
                      }`}>
                      {/* Top accent stripe */}
                      {item.highlight && (
                        <div className="h-0.5 bg-gradient-to-r from-amber-500 via-yellow-400 to-orange-500" />
                      )}
                      <div className="p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          {/* Mobile icon */}
                          <div className={`md:hidden w-10 h-10 rounded-xl flex items-center justify-center shrink-0
                            ${item.highlight ? "bg-amber-900/50 border border-amber-700/50 text-amber-300" : "bg-neutral-800 border border-neutral-700 text-amber-500"}`}>
                            {emojiToIcon[item.icon]}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className={`font-bold text-base leading-tight ${item.highlight ? "text-amber-200" : "text-neutral-200"}`}>
                                {item.name}
                              </h3>
                              {item.highlight && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-600/20 text-amber-400 border border-amber-600/30 uppercase tracking-wider">
                                  <Sparkles className="h-2.5 w-2.5" />
                                  {language === "ta" ? "சிறப்பு" : "Special"}
                                </span>
                              )}
                            </div>
                            <p className="text-neutral-500 text-xs leading-relaxed">{item.desc}</p>
                            <p className="text-neutral-700 text-xs mt-1">{item.tamil}</p>
                          </div>

                          {/* Time pill */}
                          <div className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border font-mono font-bold text-sm
                            ${item.highlight
                              ? "bg-amber-950/50 border-amber-700/40 text-amber-300"
                              : "bg-neutral-800/80 border-neutral-700 text-neutral-300"
                            }`}>
                            <Clock className={`h-3.5 w-3.5 ${item.highlight ? "text-amber-500" : "text-neutral-500"}`} />
                            {item.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary band */}
            <div className="max-w-3xl mx-auto grid grid-cols-2 gap-3">
              {[
                { label: language === "ta" ? "காலை திறப்பு" : "Morning Open", time: "4:00 AM", icon: <Sunrise className="h-4 w-4" />, col: "from-amber-950/40 to-neutral-900 border-amber-800/40" },
                { label: language === "ta" ? "மாலை மூடல்" : "Evening Close", time: "7:00 PM", icon: <Moon className="h-4 w-4" />, col: "from-neutral-900 to-neutral-900/50 border-neutral-800" },
              ].map((s, i) => (
                <div key={i} className={`flex items-center gap-4 p-5 rounded-2xl border bg-gradient-to-r ${s.col}`}>
                  <div className="w-10 h-10 rounded-xl bg-amber-950/50 border border-amber-900/40 flex items-center justify-center text-amber-400 shrink-0">{s.icon}</div>
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-widest">{s.label}</p>
                    <p className="text-amber-300 font-mono font-bold text-xl">{s.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── WEEKLY ── */}
        {activeTab === "weekly" && (
          <div className="space-y-12">
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-600/50" />
                <CalendarDays className="h-4 w-4 text-amber-500" />
                <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-600/50" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-400">{t.weekly.title}</h2>
              <p className="text-neutral-500 text-sm">{t.weekly.subtitle}</p>
            </div>

            {/* Weekly dot calendar */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-1.5 p-3 rounded-2xl border border-neutral-800 bg-neutral-900/40">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => {
                  const isSpecial = i === 2 || i === 5;
                  return (
                    <div key={i} className={`flex flex-col items-center gap-1.5 w-9 h-14 rounded-xl justify-center transition-all
                      ${isSpecial ? "bg-amber-600/20 border border-amber-700/50" : "bg-neutral-900 border border-neutral-800"}`}>
                      <span className={`text-xs font-bold ${isSpecial ? "text-amber-400" : "text-neutral-500"}`}>{d}</span>
                      {isSpecial && <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/60" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Day cards grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {t.weekly.days.map((day, i) => (
                <div key={i} className={`group relative rounded-3xl border transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl overflow-hidden
                  ${day.highlight
                    ? "border-amber-700/50 bg-gradient-to-br from-amber-950/40 to-neutral-900 shadow-lg shadow-amber-950/30"
                    : "border-neutral-800 bg-neutral-900/50 hover:border-neutral-700"
                  }`}>
                  {/* Highlight stripe */}
                  {day.highlight && <div className="h-0.5 bg-gradient-to-r from-amber-500 via-yellow-300 to-orange-500" />}

                  {/* Number watermark */}
                  <span className="absolute top-4 right-5 text-7xl font-black text-neutral-800/40 group-hover:text-amber-900/25 transition-colors leading-none select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="relative z-10 p-7 space-y-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-transform group-hover:scale-110
                          ${day.highlight
                            ? "bg-amber-900/40 border-amber-700/50 text-amber-300"
                            : "bg-neutral-800 border-neutral-700 text-amber-500"
                          }`}>
                          {emojiToIcon[day.icon]}
                        </div>
                        <div>
                          <h3 className={`text-xl font-bold ${day.highlight ? "text-amber-200" : "text-neutral-100"}`}>
                            {day.day}
                          </h3>
                          <p className="text-xs text-neutral-600">{day.tamilDay}</p>
                        </div>
                      </div>
                      {day.highlight && <Star className="h-4 w-4 text-amber-500 fill-amber-500/30" />}
                    </div>

                    <div className="h-px bg-neutral-800 group-hover:bg-amber-900/30 transition-colors" />

                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border
                      ${day.highlight
                        ? "bg-amber-950/50 text-amber-400 border-amber-800/40"
                        : "bg-neutral-800/80 text-neutral-400 border-neutral-700"
                      }`}>
                      <Clock className="h-3 w-3" />
                      {day.timing}
                    </div>

                    <p className="text-neutral-500 text-sm leading-relaxed">{day.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-amber-700/60 text-xs tracking-widest uppercase flex items-center justify-center gap-2">
              <Sparkles className="h-3 w-3" />
              {language === "ta" ? "செவ்வாய் & வெள்ளி -  சிறப்பு பூஜை நாட்கள்" : "Tuesday & Friday -  Special Pooja Days"}
              <Sparkles className="h-3 w-3" />
            </p>
          </div>
        )}

        {/* ── MONTHLY ── */}
        {activeTab === "monthly" && (
          <div className="space-y-12">
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-600/50" />
                <Moon className="h-4 w-4 text-amber-500" />
                <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-600/50" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-400">{t.monthly.title}</h2>
              <p className="text-neutral-500 text-sm">{t.monthly.subtitle}</p>
            </div>

            {/* Lunar phase display */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-3 p-5 rounded-3xl border border-neutral-800 bg-neutral-900/40">
                {(["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"] as const).map((phase, i) => {
                  const opacities = ["opacity-70", "opacity-40", "opacity-55", "opacity-70", "opacity-100", "opacity-70", "opacity-55", "opacity-40"];
                  const scales = i === 4 ? "scale-125" : i === 0 ? "scale-110" : "scale-100";
                  return (
                    <div key={i} className={`transition-all ${opacities[i]} ${scales} text-amber-400`}>
                      {emojiToIcon[phase]}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {t.monthly.items.map((item, i) => (
                <div key={i} className={`group relative rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden
                  ${monthlyColorBorder[item.color] || "border-neutral-800 bg-neutral-900/50"}`}>
                  <div className="p-8 space-y-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-amber-950/60 border border-amber-900/50 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                          {emojiToIcon[item.icon]}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-neutral-100">{item.name}</h3>
                          <p className="text-xs text-neutral-600 mt-0.5">{item.tamilName}</p>
                        </div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800/60" />

                    <div className="space-y-3">
                      <span className="inline-block text-xs px-3 py-1 rounded-full bg-neutral-800/80 text-neutral-500 border border-neutral-700">{item.type}</span>
                      <p className="text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>

                    <div className="flex items-center gap-2 bg-amber-950/30 border border-amber-900/30 rounded-xl px-4 py-3">
                      <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                      <span className="font-mono font-bold text-amber-400 text-sm">{item.timing}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── AMAVASAI ── */}
        {activeTab === "amavasai" && (
          <div className="space-y-12">
            {/* Header with moon icon */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full border-2 border-amber-700/50 bg-amber-950/30 flex items-center justify-center shadow-2xl shadow-amber-900/40">
                  <Moon className="h-10 w-10 text-amber-400" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-400">{t.amavasai.title}</h2>
              <p className="text-neutral-500 text-sm">{t.amavasai.subtitle}</p>
            </div>

            {/* Significance panel */}
            <div className="max-w-3xl mx-auto p-7 rounded-3xl border border-amber-800/40 bg-gradient-to-br from-amber-950/30 to-neutral-900 flex gap-5 items-start">
              <div className="w-12 h-12 rounded-2xl bg-amber-900/40 border border-amber-800/40 flex items-center justify-center text-amber-400 shrink-0">
                <Info className="h-5 w-5" />
              </div>
              <p className="text-neutral-300 leading-relaxed text-[15px]">{t.amavasai.significance}</p>
            </div>

            {/* Schedule -  vertical timeline, full width */}
            <div className="max-w-2xl mx-auto relative">
              {/* Center spine */}
              <div className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-amber-600/50 via-amber-800/20 to-transparent" />

              <div className="space-y-4">
                {t.amavasai.schedule.map((item, i) => (
                  <div key={i} className="flex gap-5 items-stretch group">
                    {/* Node */}
                    <div className="shrink-0 flex flex-col items-center pt-4">
                      <div className={`relative z-10 w-12 h-12 rounded-2xl border-2 flex items-center justify-center transition-all duration-300 group-hover:scale-110
                        bg-gradient-to-br ${timelineColors[i % timelineColors.length]}
                        border-amber-600/60 text-white shadow-lg shadow-amber-900/30`}>
                        {emojiToIcon[item.icon]}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 rounded-2xl border border-neutral-800 bg-neutral-900/50 hover:border-amber-800/30 hover:bg-neutral-900/70 transition-all duration-300 p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1">
                          <span className="inline-block font-mono text-amber-400 font-bold text-sm bg-amber-950/40 border border-amber-900/40 px-3 py-1 rounded-full mb-2">
                            {item.time}
                          </span>
                          <h4 className="font-bold text-neutral-200 text-base leading-tight">{item.event}</h4>
                          <p className="text-neutral-500 text-sm mt-1 leading-relaxed">{item.desc}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-neutral-700 group-hover:text-amber-600 group-hover:translate-x-1 transition-all shrink-0" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── FESTIVALS ── */}
        {activeTab === "festivals" && (
          <div className="space-y-12">
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-600/50" />
                <Sparkles className="h-4 w-4 text-amber-500" />
                <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-600/50" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-400">{t.festivals.title}</h2>
              <p className="text-neutral-500 text-sm">{t.festivals.subtitle}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {t.festivals.items.map((item, i) => (
                <div key={i} className="group relative rounded-3xl border border-neutral-800 bg-neutral-900/40 hover:border-amber-800/40 hover:bg-gradient-to-br hover:from-amber-950/25 hover:to-neutral-900 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-amber-950/30 overflow-hidden">
                  {/* Number watermark */}
                  <span className="absolute top-4 right-5 text-6xl font-black text-neutral-800/50 group-hover:text-amber-900/25 transition-colors leading-none select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="relative z-10 p-7 space-y-5">
                    <div className="flex items-start justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-amber-950/50 border border-amber-900/40 flex items-center justify-center text-amber-400 group-hover:scale-110 group-hover:border-amber-700/60 transition-all duration-300">
                        {emojiToIcon[item.icon]}
                      </div>
                      <span className={`text-xs border px-2.5 py-1 rounded-full font-semibold
                        ${freqColorMap[item.freq] || "bg-neutral-800 text-neutral-400 border-neutral-700"}`}>
                        {item.freq}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-neutral-100 group-hover:text-amber-200 transition-colors">{item.name}</h3>
                      <p className="text-xs text-neutral-600 mt-0.5">{item.tamilName}</p>
                    </div>

                    <div className="h-px bg-neutral-800 group-hover:bg-amber-900/40 transition-colors" />

                    <p className="text-neutral-500 text-sm leading-relaxed">{item.desc}</p>

                    <div className="flex items-center gap-2 bg-neutral-800/60 border border-neutral-700 rounded-xl px-4 py-2.5 group-hover:bg-amber-950/30 group-hover:border-amber-900/40 transition-all">
                      <Clock className="h-3.5 w-3.5 text-amber-600" />
                      <span className="font-mono text-amber-400 font-semibold text-xs">{item.timing}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── CONTACT FOOTER ── */}
      <section className="border-t border-amber-900/20 bg-gradient-to-t from-amber-950/10 to-transparent py-12">
        <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/20 to-transparent" />
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-10 bg-amber-900/40" />
              <Heart className="h-3.5 w-3.5 text-amber-700" />
              <p className="text-neutral-500 text-sm">{t.contactNote}</p>
              <Heart className="h-3.5 w-3.5 text-amber-700" />
              <div className="h-px w-10 bg-amber-900/40" />
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {["9597255401", "9361986095"].map((num, i) => (
                <a key={i} href={`tel:${num}`} className="group">
                  <div className="flex items-center gap-3 px-6 py-3.5 rounded-2xl border border-neutral-800 bg-neutral-900/60 hover:border-amber-700/50 hover:bg-amber-950/25 transition-all duration-300 hover:-translate-y-0.5">
                    <div className="w-8 h-8 rounded-xl bg-amber-950/50 border border-amber-900/40 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                      <Phone className="h-4 w-4" />
                    </div>
                    <span className="text-amber-300 font-mono tracking-wider text-sm font-semibold">+91 {num}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-neutral-700">
              <MapPin className="h-3 w-3" />
              <span>Sukkampatti, Kadavoor Thaluk, Karur District, Tamil Nadu</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}